import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { checkZoomTokenStatus, getZoomAuthUrl } from "../../api/zoom";
import {
  getCalendlyAuthUrl, getCalendlyTherawinData
} from "../../api/calendly";
import { Calendar, Video } from "lucide-react";
import { Button } from "../../components/button";
import { jwtDecode } from "jwt-decode";

const checkJwtValidity = (jwt) => {
  let decodedToken = jwtDecode(jwt);
  let currentDate = new Date();
  if (decodedToken.exp * 1000 < currentDate.getTime()) {
    return false;
  } else {
    return true;
  }
};

function ZoomSettingsPage() {
  const [zoomStatus, setZoomStatus] = useState("Checking...");
  const [calendlyStatus, setCalendlyStatus] = useState("Checking...");
  const [calendlyEmail, setCalendlyEmail] = useState("Checking...");
  const [calendlyWebhookStatus, setCalendlyWebhookStatus] = useState(null);
  const therapist_id = useSelector((store) => store.therapist.therapist_id);

  useEffect(() => {
    // Check for Calendly webhook status from redirect
    const webhookStatusRaw = sessionStorage.getItem("calendly_webhook_status");
    if (webhookStatusRaw) {
      try {
        const webhookStatus = JSON.parse(webhookStatusRaw);
        setCalendlyWebhookStatus(webhookStatus);
      } catch (e) {
        setCalendlyWebhookStatus({
          status: "error",
          message: "Could not parse Calendly webhook status.",
        });
      }
      sessionStorage.removeItem("calendly_webhook_status");
    }

    const fetchZoomTokenStatus = async () => {
      try {
        const res = await checkZoomTokenStatus(therapist_id);

        const { data } = res;
        const { hasToken, refreshTokenExpired } = data;
        console.log("hasToken", hasToken);
        console.log("refreshTokenExpired", refreshTokenExpired);
        if (!hasToken) {
          setZoomStatus("Not Connected");
        } else if (hasToken && !refreshTokenExpired) {
          setZoomStatus("Connected");
        } else if (hasToken && refreshTokenExpired) {
          setZoomStatus("Token Expired");
        }
      } catch (error) {
        console.error("Error checking Zoom token status:", error);
        setZoomStatus("Not Connected");
      }
    };

    const fetchCalendlyTokenStatus = async () => {
      try {
        const {
          data: { access_token: calendlyAccessToken, webhook_is_registered: webhookIsRegistered, calendly_email: calendlyEmail },
        } = await getCalendlyTherawinData(therapist_id);
        setCalendlyEmail(calendlyEmail);
        const isValid = checkJwtValidity(calendlyAccessToken);
        if (!calendlyAccessToken) {
          setCalendlyStatus("Not Connected");
        } else if (isValid) {
          setCalendlyStatus("Connected");
        } else if (!isValid) {
          setCalendlyStatus(`Token Expired But last known status is ${webhookIsRegistered ? "Registered" : "Not Registered"}`);
        }
      } catch (error) {
        console.log("Error checking Calendly token status:", error);
        setCalendlyStatus("Not Connected");
      }
    };

    fetchZoomTokenStatus();
    fetchCalendlyTokenStatus();
  }, []);

  // Handler from snippet #1 (renamed for clarity):
  const handleConnectZoom = async () => {
    try {
      const res = await getZoomAuthUrl();
      const data = res.data;
      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        alert("No authUrl returned from server.");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to initiate Zoom OAuth.");
    }
  };

  const handleCalendlyConnect = async () => {
    try {
      const res = await getCalendlyAuthUrl();
      console.log("Calendly Auth URL Response:", res);
      const data = res.data;
      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        alert("No authUrl returned from server.");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to initiate Calendly OAuth.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 w-full">
      {/* Outer Container from snippet #2 */}
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header Section from snippet #2 */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
          <p className="text-gray-600 mt-1">
            Manage your external app connections
          </p>
        </div>

        {/* Main Content from snippet #3 (Zoom Integration Card) */}
        <div className="bg-white shadow rounded-lg">
          {/* Card Header */}
          <div className="px-6 py-4 border-b">
            <div className="flex items-center">
              <Video className="h-6 w-6 text-gray-400 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Zoom Integration
              </h3>
            </div>
          </div>

          {/* Card Body */}
          <div className="px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                <p className="text-sm text-gray-900">{zoomStatus}</p>
              </div>
              <Button
                className="flex items-center justify-center gap-2"
                onClick={handleConnectZoom}
              >
                <Video className="h-4 w-4" />
                {zoomStatus === "Connected"
                  ? "Re-connect Account"
                  : "Connect Account"}
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          {/* Card Header */}
          <div className="px-6 py-4 border-b">
            <div className="flex items-center">
              <Calendar className="h-6 w-6 text-gray-400 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Calendly Integration
              </h3>
            </div>
          </div>

          {/* Card Body */}
          <div className="px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                <p className="text-sm text-gray-900">{calendlyStatus}</p>
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p className="text-sm text-gray-900">{calendlyEmail}</p>
              </div>
              <Button
                className="flex items-center justify-center gap-2"
                onClick={handleCalendlyConnect}
              >
                <Calendar className="h-4 w-4" />
                {calendlyStatus === "Connected"
                  ? "Re-connect Account"
                  : "Connect Account"}
              </Button>
            </div>
          </div>
        </div>
        {/* Calendly Webhook Status Banner */}
        {calendlyWebhookStatus && (
          <div
            className={`px-6 py-4 mb-4 rounded-md border ${
              calendlyWebhookStatus.status === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            {calendlyWebhookStatus.message}
          </div>
        )}
      </div>
    </div>
  );
}

export default ZoomSettingsPage;
